import React, { useState, useEffect } from 'react';
import { AccountIcon, UploadCloudIcon, CautionIcon } from '../../../public/icons';
import adminService from '../../../services/admin/admin-authService';
import { useAdminAuth } from '../../../context/AdminAuthContext'; // Or wherever your context is

const ProfileSettingsTab = () => {
  const { admin, updateProfile, changePassword } = useAdminAuth();
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    role: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Populate form with admin data when component mounts or admin changes
  useEffect(() => {
    if (admin) {
      setProfileData({
        fullName: admin.name || '',
        email: admin.email || '',
        phoneNumber: admin.phoneNumber || '',
        role: admin.role || ''
      });
    }
  }, [admin]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear any previous save messages
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Only JPG, PNG, or GIF files are allowed');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setSaveSuccess(false);
        setSaveError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSaveChanges = async () => {
    if (!profileData.fullName.trim()) {
      setSaveError('Full name is required');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');

    try {
      // Prepare data for API
      const updateData = {
        name: profileData.fullName.trim(),
        phoneNumber: profileData.phoneNumber.trim()
      };

      // Call updateProfile from auth context
      const result = await updateProfile(updateData);

      if (result.success) {
        setSaveSuccess(true);
        setSaveError('');
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
      } else {
        setSaveError(result.message || 'Failed to save changes');
      }
    } catch (error) {
      setSaveError('An error occurred while saving. Please try again.');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    setPasswordError('');
    setPasswordResetSent(false);

    try {
      // In a real implementation, you would:
      // 1. Send a request to the server to generate a password reset token
      // 2. The server would send an email with reset instructions
      
      // For now, we'll use a placeholder
      const response = await fetch('/api/v1/admin/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminService.getToken()}`
        },
        body: JSON.stringify({ email: profileData.email })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordResetSent(true);
        setPasswordError('');
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setPasswordResetSent(false);
        }, 5000);
      } else {
        setPasswordError(data.message || 'Failed to send password reset email');
      }
    } catch (error) {
      setPasswordError('Unable to process password reset request');
      console.error('Password reset error:', error);
    }
  };

  // Show loading state while fetching admin data
  if (!admin) {
    return (
      <div className="max-w-4xl">
        <div className="bg-white p-6 mb-5">
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-gray-600">Loading profile...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Account Information Section */}
      <div className="bg-white p-6 mb-5">
        <div className="flex items-center text-left justify-start gap-3 mb-6">
          <AccountIcon className="w-6 h-6 text-[#1C2A46]" />
          <div>
            <h2 className="text-xl font-medium text-[#0E1628]">Account Information</h2>
            <p className="text-[#6B635D] text-sm">Manage your personal admin information.</p>
          </div>
        </div>

        {/* Profile Picture Section */}
        <div className="mb-8">
          <div className="flex items-start gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <AccountIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#55000C] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#440009] transition-colors">
                <UploadCloudIcon className="w-4 h-4 text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
            <div>
              <h3 className="font-medium text-[#0E1628] mb-1 text-left">Profile Picture</h3>
              <p className="text-sm text-[#6B635D] font-light text-left mb-2">JPG, PNG or GIF. Max size 2MB.</p>
              {profileImage && (
                <button
                  onClick={removeImage}
                  className="text-sm text-[#55000C] hover:text-[#440009] font-light"
                >
                  Remove Picture
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
          <div>
            <label className="block text-base font-normal text-[#0E1628] mb-2">Full Name</label>
            <input
              type="text"
              value={profileData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className="w-full p-3 border border-[#DBDBDB] font-light text-sm rounded bg-transparent focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent"
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-base font-normal text-[#0E1628] mb-2">Email Address</label>
            <input
              type="email"
              value={profileData.email}
              readOnly
              className="w-full p-3 border border-[#DBDBDB] font-light text-sm rounded bg-[#F9F9F9] text-[#6B635D] cursor-not-allowed"
            />
            <div className="flex items-center gap-2 mt-2">
              <CautionIcon className="w-3 h-3 text-[#6B635D]" />
              <span className="text-sm text-[#6B635D]">Email address cannot be changed</span>
            </div>
          </div>

          <div>
            <label className="block text-base font-normal text-[#0E1628] mb-2">Phone Number</label>
            <input
              type="tel"
              value={profileData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className="w-full p-3 border border-[#DBDBDB] font-light text-sm rounded bg-transparent focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent"
              placeholder="+234 801 234 5678"
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-base font-normal text-[#0E1628] mb-2">Role</label>
            <input
              type="text"
              value={profileData.role}
              readOnly
              className="w-full p-3 border border-[#DBDBDB] font-light text-sm rounded bg-[#F9F9F9] text-[#6B635D] cursor-not-allowed"
            />
          </div>
        </div>

        {/* Save Button and Status Messages */}
        <div className="flex justify-start items-center gap-4">
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className={`px-8 py-3 bg-primary text-white font-medium rounded hover:bg-[#C49660] transition-colors ${
              isSaving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          
          {saveSuccess && (
            <div className="text-green-600 text-sm">
              ✓ Profile updated successfully
            </div>
          )}
          
          {saveError && (
            <div className="text-red-600 text-sm">
              {saveError}
            </div>
          )}
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-white rounded-sm p-6 text-left">
        <div className="flex items-center gap-3 mb-6">
          <AccountIcon className="w-6 h-6 text-[#1C2A46]" />
          <div>
            <h2 className="text-xl font-medium text-[#0E1628]">Password</h2>
            <p className="text-[#6B635D] font-light text-sm">
              For security reasons, password changes require verification. Click below to receive a password reset link via email.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <button
            onClick={handlePasswordReset}
            disabled={passwordResetSent}
            className={`px-6 py-3 border border-[#DBDBDB] text-[#0E1628] font-medium rounded hover:bg-gray-50 transition-colors w-fit ${
              passwordResetSent ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {passwordResetSent ? 'Email Sent ✓' : 'Request Password Change'}
          </button>
          
          {passwordResetSent && (
            <div className="text-green-600 text-sm">
              Password reset email sent to {profileData.email}. Check your inbox for further instructions.
            </div>
          )}
          
          {passwordError && (
            <div className="text-red-600 text-sm">
              {passwordError}
            </div>
          )}
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="bg-white rounded-sm p-6 mt-5 text-left">
        <h3 className="text-lg font-medium text-[#0E1628] mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[#6B635D]">Account Status</p>
            <p className="font-medium text-[#0E1628]">{admin.status || 'Active'}</p>
          </div>
          <div>
            <p className="text-[#6B635D]">Last Login</p>
            <p className="font-medium text-[#0E1628]">
              {admin.lastLogin ? new Date(admin.lastLogin).toLocaleString() : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-[#6B635D]">Account Created</p>
            <p className="font-medium text-[#0E1628]">
              {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          {admin.invitedBy && (
            <div>
              <p className="text-[#6B635D]">Invited By</p>
              <p className="font-medium text-[#0E1628]">
                {admin.invitedBy?.name || 'N/A'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsTab;